/**
 * Garmin Connection Drawer
 * Simplified connection UI: Connect → Green dot + Connected + Disconnect
 * Brand attribution preserved via GarminAttribution component
 */

import React, { useState, useEffect } from 'react';
import { GarminAttribution } from '../GarminAttribution';
import garminService, { GarminConnectionStatus } from '../../services/garminService';

interface GarminDrawerProps {
  onClose: () => void;
}

export const GarminDrawer: React.FC<GarminDrawerProps> = ({ onClose }) => {
  const [status, setStatus] = useState<GarminConnectionStatus>({ connected: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deviceModel, setDeviceModel] = useState<string | null>(null);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  useEffect(() => {
    loadConnectionStatus();
  }, []);

  const loadConnectionStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const connectionStatus = await garminService.getConnectionStatus();
      setStatus(connectionStatus);
      
      if (connectionStatus.connected && connectionStatus.device_name) {
        setDeviceModel(connectionStatus.device_name);
      }
    } catch (err) {
      console.error('Failed to load Garmin connection status:', err);
      setError('Failed to load Garmin connection status');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      setError(null);
      await garminService.connect();
      await loadConnectionStatus();
    } catch (err) {
      console.error('Failed to connect Garmin:', err);
      setError('Failed to connect to Garmin. Please try again.');
    }
  };

  const handleDisconnect = async () => {
    try {
      setError(null);
      setLoading(true);
      setShowDisconnectConfirm(false);
      await garminService.disconnect();
      await loadConnectionStatus();
    } catch (err) {
      console.error('Failed to disconnect Garmin:', err);
      setError('Failed to disconnect Garmin. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-sheet-panel">
        <div className="settings-sheet-panel-title">Garmin Connect</div>
        <div className="loading-spinner" style={{ margin: '40px auto', display: 'block' }}>
          Loading connection status...
        </div>
      </div>
    );
  }

  return (
    <div className="settings-sheet-panel">
      <div className="settings-sheet-panel-title">Garmin Connect</div>
      
      {/* Garmin logo only – preserve aspect ratio (no device text in header) */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'center' }}>
        <div style={{ maxWidth: 140, display: 'flex', justifyContent: 'center' }}>
          <GarminAttribution theme="light" size="medium" />
        </div>
      </div>

      {error && (
        <div className="error-message" style={{
          padding: '12px',
          marginBottom: '16px',
          backgroundColor: '#fee',
          color: '#c33',
          borderRadius: '8px',
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}

      {status.garmin_available === false ? (
        /* ── Garmin light switch OFF: Coming Soon state ── */
        <div style={{ opacity: 0.6 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '12px 16px',
            backgroundColor: '#f0f0f0',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <span style={{
              fontWeight: '600',
              color: '#888',
              fontSize: '16px'
            }}>
              Coming Soon
            </span>
          </div>
          <p style={{
            color: '#888',
            lineHeight: '1.6',
            fontSize: '15px',
            textAlign: 'center'
          }}>
            Garmin Connect™ integration is currently being finalized and approved by Garmin. We'll notify you as soon as it's available.
          </p>
        </div>
      ) : status.connected ? (
        <div className="garmin-connected-state">
          {/* Connection Status Badge */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '12px 16px',
            backgroundColor: '#e8f5e9',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <span style={{
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              backgroundColor: '#4caf50',
              display: 'inline-block'
            }}></span>
            <span style={{
              fontWeight: '600',
              color: '#2e7d32',
              fontSize: '16px'
            }}>
              Connected
            </span>
          </div>

          {/* Garmin User and Device – only show when available */}
          {(status.display_name || status.device_name) && (
            <div style={{
              marginBottom: '20px',
              padding: '12px',
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              fontSize: '15px',
              color: '#333'
            }}>
              {status.display_name && (
                <div style={{ marginBottom: status.device_name ? '6px' : 0 }}>
                  Garmin User: {status.display_name}
                </div>
              )}
              {status.device_name && (
                <div style={{ fontSize: '14px', color: '#666' }}>
                  Device: {status.device_name}
                </div>
              )}
            </div>
          )}

          {/* Disconnect Button */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {!showDisconnectConfirm ? (
              <button
                onClick={() => setShowDisconnectConfirm(true)}
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  backgroundColor: 'transparent',
                  color: '#666',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Disconnect
              </button>
            ) : (
              <div style={{
                padding: '14px',
                backgroundColor: '#fff3cd',
                borderRadius: '8px',
                border: '1px solid #ffc107'
              }}>
                <div style={{ fontSize: '14px', color: '#856404', marginBottom: '12px', fontWeight: '500' }}>
                  Are you sure you want to disconnect from Garmin Connect?
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={handleDisconnect}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Disconnect
                  </button>
                  <button
                    onClick={() => setShowDisconnectConfirm(false)}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      backgroundColor: '#f5f5f5',
                      color: '#333',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="garmin-disconnected-state">
          <p style={{
            marginBottom: '24px',
            color: '#666',
            lineHeight: '1.6',
            fontSize: '15px'
          }}>
            Link Garmin Connect to automatically sync your workouts, runs, rides, and other activities to Coach Cedar.
          </p>

          <button
            onClick={handleConnect}
            style={{
              width: '100%',
              padding: '14px 24px',
              backgroundColor: '#1F6C4F',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Connect Garmin
          </button>
        </div>
      )}
    </div>
  );
};
